// src/services/incidentsService.ts
// Fetches live incidents from the Cloudflare Worker API (Neon database).
// Maps the WHS schema's incident shape to the Incident interface Incidents.tsx expects.
//
// Schema ↔ Interface mapping notes:
//   incidents.occurredAt      → Incident.dateTime
//   incidents.severity        → split into Incident.type + Incident.severity
//     (DB stores NEAR_MISS/FIRST_AID/MEDICAL_TREATMENT/LOST_TIME/CRITICAL/FATALITY;
//      the UI separates type: INCIDENT|NEAR_MISS|HAZARD_OBSERVATION|PROPERTY_DAMAGE
//      from severity: LOW|MEDIUM|HIGH|CRITICAL)
//   incidents.immediateAction → Incident.immediateActions
//   incidents.regulatorNotified → Incident.notifiableToWorksafe
//   incidents.regulatorNotifiedAt → Incident.worksafeNotifiedAt
//   incidents.status CORRECTIVE_ACTION|REGULATOR_NOTIFIED → mapped to UNDER_INVESTIGATION
//   incidentNumber: not in DB schema — generated from id suffix

import { api } from '../utils/api'

// What the API returns — matches the incidents table schema
interface ApiIncident {
  id:                  string
  jobId:               string
  job_id?:             string
  title:               string
  description?:        string | null
  severity:            string   // NEAR_MISS | FIRST_AID | MEDICAL_TREATMENT | LOST_TIME | CRITICAL | FATALITY
  status:              string   // OPEN | UNDER_INVESTIGATION | CORRECTIVE_ACTION | CLOSED | REGULATOR_NOTIFIED
  location?:           string | null
  occurredAt?:         string | null
  occurred_at?:        string | null
  reportedById?:       string | null
  reported_by_id?:     string | null
  injuredParty?:       string | null
  injured_party?:      string | null
  witnessNames?:       string[] | null
  witness_names?:      string[] | null
  immediateAction?:    string | null
  immediate_action?:   string | null
  rootCause?:          string | null
  root_cause?:         string | null
  regulatorNotified?:  boolean
  regulator_notified?: boolean
  regulatorNotifiedAt?: string | null
  regulator_notified_at?: string | null
  swmsReviewRequired?: boolean
  closedAt?:           string | null
  closed_at?:          string | null
  createdAt?:          string
  created_at?:         string
}

// What Incidents.tsx expects — the local Incident interface
export interface MappedIncident {
  id:                   string
  incidentNumber:       string
  type:                 'INCIDENT' | 'NEAR_MISS' | 'HAZARD_OBSERVATION' | 'PROPERTY_DAMAGE'
  severity:             'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  jobId:                string
  jobNumber:            string
  dateTime:             string
  location:             string
  description:          string
  immediateActions:     string
  reportedByName:       string
  witnesses:            string[]
  treatmentRequired:    'NONE' | 'FIRST_AID' | 'MEDICAL' | 'HOSPITAL'
  status:               'OPEN' | 'UNDER_INVESTIGATION' | 'CLOSED'
  investigationNotes:   string | null
  notifiableToWorksafe: boolean
  worksafeNotifiedAt:   string | null
  worksafeRef:          string | null
  closedAt:             string | null
}

// DB severity → UI type + severity split
function mapDbSeverity(dbSeverity: string): {
  type: MappedIncident['type']
  severity: MappedIncident['severity']
  treatmentRequired: MappedIncident['treatmentRequired']
} {
  switch (dbSeverity) {
    case 'NEAR_MISS':
      return { type: 'NEAR_MISS',  severity: 'LOW',      treatmentRequired: 'NONE' }
    case 'FIRST_AID':
      return { type: 'INCIDENT',   severity: 'LOW',      treatmentRequired: 'FIRST_AID' }
    case 'MEDICAL_TREATMENT':
      return { type: 'INCIDENT',   severity: 'MEDIUM',   treatmentRequired: 'MEDICAL' }
    case 'LOST_TIME':
      return { type: 'INCIDENT',   severity: 'HIGH',     treatmentRequired: 'MEDICAL' }
    case 'CRITICAL':
      return { type: 'INCIDENT',   severity: 'CRITICAL', treatmentRequired: 'HOSPITAL' }
    case 'FATALITY':
      return { type: 'INCIDENT',   severity: 'CRITICAL', treatmentRequired: 'HOSPITAL' }
    default:
      return { type: 'INCIDENT',   severity: 'LOW',      treatmentRequired: 'NONE' }
  }
}

// DB status → UI status (collapse CORRECTIVE_ACTION and REGULATOR_NOTIFIED)
function mapDbStatus(dbStatus: string): MappedIncident['status'] {
  if (dbStatus === 'CLOSED') return 'CLOSED'
  if (dbStatus === 'UNDER_INVESTIGATION' || dbStatus === 'CORRECTIVE_ACTION') return 'UNDER_INVESTIGATION'
  if (dbStatus === 'REGULATOR_NOTIFIED') return 'UNDER_INVESTIGATION'
  return 'OPEN'
}

function mapApiIncident(raw: ApiIncident, index: number): MappedIncident {
  const { type, severity, treatmentRequired } = mapDbSeverity(raw.severity)
  const status = mapDbStatus(raw.status)

  const occurredAt = raw.occurredAt ?? raw.occurred_at ?? new Date().toISOString()
  const witnesses  = raw.witnessNames ?? raw.witness_names ?? []

  return {
    id:              raw.id,
    // Generate a reference number from the DB id suffix (avoids needing a separate sequence)
    incidentNumber:  `INC-LIVE-${raw.id.slice(-6).toUpperCase()}`,
    type,
    severity,
    jobId:           raw.jobId ?? raw.job_id ?? '',
    jobNumber:       '',                                  // enriched from jobs join in a future step
    dateTime:        occurredAt,
    location:        raw.location        ?? '',
    description:     raw.title + (raw.description ? `: ${raw.description}` : ''),
    immediateActions: raw.immediateAction ?? raw.immediate_action ?? '',
    reportedByName:  raw.reportedById    ?? raw.reported_by_id    ?? 'Unknown',
    witnesses:       Array.isArray(witnesses) ? witnesses as string[] : [],
    treatmentRequired,
    status,
    investigationNotes: raw.rootCause ?? raw.root_cause ?? null,
    notifiableToWorksafe: raw.regulatorNotified ?? raw.regulator_notified ?? false,
    worksafeNotifiedAt:   raw.regulatorNotifiedAt ?? raw.regulator_notified_at ?? null,
    worksafeRef:          null,
    closedAt:             raw.closedAt ?? raw.closed_at ?? null,
  }
}

export async function fetchIncidents(jobId?: string): Promise<MappedIncident[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiIncident[] } | ApiIncident[]>(`/api/whs/incidents${params}`)
  const raw: ApiIncident[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiIncident)
}

// Map UI severity + treatment → DB incidentSeverityEnum
function uiSeverityToDb(
  severity: 'LOW'|'MEDIUM'|'HIGH'|'CRITICAL',
  treatment: string,
): string {
  if (treatment === 'HOSPITAL')         return 'MEDICAL_TREATMENT'
  if (severity === 'CRITICAL')          return 'CRITICAL'
  if (severity === 'HIGH')              return 'LOST_TIME'
  if (treatment === 'MEDICAL')          return 'MEDICAL_TREATMENT'
  if (treatment === 'FIRST_AID')        return 'FIRST_AID'
  return 'NEAR_MISS'
}

// Fire-and-forget POST to Neon — fails silently for mock job IDs
export async function createIncident(inc: {
  id: string
  jobId: string
  description: string
  severity: string
  treatmentRequired: string
  location: string
  dateTime: string
  immediateActions: string
  reportedByName: string
}): Promise<void> {
  try {
    await api.post('/api/whs/incidents', {
      jobId:           inc.jobId,
      title:           inc.description.slice(0, 120),
      description:     inc.description,
      severity:        uiSeverityToDb(inc.severity as any, inc.treatmentRequired),
      status:          'OPEN',
      location:        inc.location,
      occurredAt:      inc.dateTime,
      immediateAction: inc.immediateActions,
    })
  } catch (err) {
    // Silently ignore failures for mock job IDs (FK constraint) or network errors
    console.warn('createIncident to Neon failed (mock job ID or network):', err)
  }
}
